import { useState, useEffect, useCallback } from 'react'
import { Loader2, ShieldCheck } from 'lucide-react'
import { InlineEditField } from '@/components/InlineEditField'
import { businessService, ComplianceFiling, FilingStatus, FilingType } from '@/services/businessService'
import { useToast } from '@/components/ui/MobileToast'

interface ComplianceTabProps {
  business: any
  onUpdate: (updated: any) => void
}

const FILING_LABELS: Record<FilingType, string> = {
  gst: 'GST Filings',
  mca: 'MCA Filings',
  itr: 'ITR Status',
}

const STATUS_OPTIONS = [
  { value: 'up_to_date', label: 'Up to Date' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'not_applicable', label: 'Not Applicable' },
]

const STATUS_COLORS: Record<FilingStatus, string> = {
  up_to_date: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
  overdue: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
  not_applicable: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600',
}

const STATUS_DOT_COLORS: Record<FilingStatus, string> = {
  up_to_date: 'bg-green-500',
  pending: 'bg-yellow-500',
  overdue: 'bg-red-500',
  not_applicable: 'bg-gray-400',
}

export function ComplianceTab({ business, onUpdate: _onUpdate }: ComplianceTabProps) {
  const { showToast } = useToast()
  const [filings, setFilings] = useState<ComplianceFiling[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadCompliance = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await businessService.getCompliance(business.id)
      setFilings(data)
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load compliance data.',
      })
    } finally {
      setIsLoading(false)
    }
  }, [business.id, showToast])

  useEffect(() => {
    loadCompliance()
  }, [loadCompliance])

  const handleSave = useCallback(
    async (fieldName: string, value: string | number) => {
      // fieldName is the filing_type, value is the new status
      const filingType = fieldName as FilingType
      const newStatus = value as FilingStatus

      try {
        const updatedFilings = await businessService.updateCompliance(business.id, {
          filings: [{ filing_type: filingType, status: newStatus }],
        })
        setFilings(updatedFilings)

        // Check for badge changes
        if ((updatedFilings as any).badge_awarded) {
          showToast({
            type: 'success',
            title: '🏆 Badge Earned!',
            message: `Congratulations! You earned the "${(updatedFilings as any).badge_awarded}" badge.`,
          })
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.error || err?.message || 'Save failed — please try again'
        throw new Error(message)
      }
    },
    [business.id, showToast]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading compliance data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-5 h-5 text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Compliance Filings
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(['gst', 'mca', 'itr'] as FilingType[]).map((filingType) => {
          const filing = filings.find((f) => f.filing_type === filingType)
          const status: FilingStatus = filing?.status || 'not_applicable'

          return (
            <div
              key={filingType}
              className={`rounded-lg border p-5 ${STATUS_COLORS[status]}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT_COLORS[status]}`} />
                <h4 className="font-medium text-sm uppercase tracking-wide">
                  {FILING_LABELS[filingType]}
                </h4>
              </div>

              <InlineEditField
                label="Status"
                value={status}
                fieldName={filingType}
                type="select"
                options={STATUS_OPTIONS}
                onSave={handleSave}
              />

              {filing?.updated_at && (
                <p className="mt-2 text-xs opacity-70">
                  Last updated: {new Date(filing.updated_at).toLocaleDateString('en-IN')}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
